import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Tabs,
  Anchor,
  Title,
  Box,
  Button,
  Center,
  Loader,
  Group,
  Divider,
} from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconBrandLinkedin,
  IconExternalLink,
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import DocumentsTab from "@/components/shared/AnonymousCompanyProfile/DocumentsTab";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import VerificationStatusBadge from "../../AdminStausBadge";
import AdminCollaborationsTab from "./CollaborationTab";
import AdminInterestsTab from "./InterestRequestTab";
import AdminCreditHistoryTab from "./CreditHistoryTab";
import AdminSignupLegalAcceptanceTab from "./AdminSignupLegalAcceptanceTab";
import AdminCompanyAuthorityAcceptanceTab from "./AdminCompanyAuthorityAcceptanceTab";
import VerifyCompanyModal from "./VerifyModal";
import { successNotification } from "@/shared/utils/successNotification";
import RejectCompanyModal from "./RejectModal";
import { formatHeadquartersLocationLine } from "@/shared/constants/signup.const";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TAB_LABEL_FZ,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
} from "@/shared/styles/navTabsLayout";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";

export default function AdminCompanyDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const [verifyModalOpened, { open: openVerifyModal, close: closeVerifyModal }] = useDisclosure(false);
  const [verifyDirectlyModalOpened, { open: openVerifyDirectlyModal, close: closeVerifyDirectlyModal }] = useDisclosure(false);
  const [rejectModalOpened, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [disableModalOpened, { open: openDisableModal, close: closeDisableModal }] = useDisclosure(false);

  const { tabsListStyles: adminCompanyNavTabsListStyles, tabsRootStyles: adminCompanyNavTabsRootStyles } =
    useNavTabsStackLayout(7);

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  useEffect(() => {
    if (!id) return;

    const fetchCompanyData = async () => {
      setLoading(true);
      try {
        const companyData = await trpc.admin.getById.query({ companyId: id });
        setCompany(companyData);
      } catch (err) {
        console.error("Error fetching company:", err);
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  const handleVerify = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const result = await trpc.admin.companyApprove.mutate({ companyId: id });

      // Update local state with the result from backend
      setCompany((prev: any) => ({
        ...prev,
        status: result.status,
        adminComments: result.adminComments,
      }));

      closeVerifyModal();

      successNotification({ title: "Profile Verified", message: "Company profile has been verified" });
    } catch (err: any) {
      console.error("Error verifying company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyDirectly = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const result = await trpc.admin.companyApprove.mutate({ companyId: id });

      setCompany((prev: any) => ({
        ...prev,
        status: result.status,
        adminComments: result.adminComments,
      }));

      closeVerifyDirectlyModal();

      successNotification({ title: "Profile Verified", message: "Company profile has been verified" });
    } catch (err: any) {
      console.error("Error verifying company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!id) return;
    setActionLoading(true);
    try {
      await trpc.admin.companyReject.mutate({ companyId: id, adminComments: reason });
      setCompany((prev: any) => ({ ...prev, status: "ACTION_REQUIRED", adminComments: reason }));
      closeRejectModal();
      successNotification({ title: "Profile Rejected", message: "Company profile has been rejected" });
    } catch (err) {
      console.error("Error rejecting company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!id) return;

    if (!company?.adminBlocked) {
      // Open modal for disable
      openDisableModal();
      return;
    }

    // Enable directly
    setBlockLoading(true);
    try {
      await trpc.admin.unblockCompany.mutate({ companyId: id });
      setCompany((prev: any) => ({ ...prev, adminBlocked: false }));
      successNotification({ title: "Profile Enabled", message: "Company profile has been enabled" });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleConfirmDisable = async (reason: string) => {
    if (!id) return;
    setBlockLoading(true);
    try {
      await trpc.admin.blockCompany.mutate({ companyId: id, reason });
      setCompany((prev: any) => ({ ...prev, adminBlocked: true }));
      closeDisableModal();
      successNotification({ title: "Profile Disabled", message: "Company profile has been disabled" });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  const showVerifyButton = company?.status === "SYSTEM_VERIFIED" || company?.status === "UNDER_REVIEW";
  const showRejectButton = company?.status === "SYSTEM_VERIFIED" || company?.status === "UNDER_REVIEW";
  const showVerifyDirectlyButton = company?.status === "DRAFT";

  if (loading) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="fluid" py="xl">
        <Text>Company not found</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Back Button */}
      <Box py="sm" px="xl">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          onClick={() => navigate("/admin/companies")}
        >
          Back to Companies
        </Button>
      </Box>

      <Container size="fluid" px="xl" pb="xs">
        {/* Company Header */}
        <Paper p="xl" radius="lg" mb="xl" style={{ border: "1px solid #04305c" }}>
          <Flex justify="space-between" align="flex-start">
            <Flex gap="md" align="flex-start">
              <Flex
                w={64}
                h={64}
                style={{
                  borderRadius: 8,
                  border: "1px solid #04305c",
                  backgroundColor: "#F9FAFB",
                  flexShrink: 0,
                }}
                align="center"
                justify="center"
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                  />
                ) : (
                  <IconBuilding size={28} color="#6B7280" />
                )}
              </Flex>

              <Flex direction="column" gap={4}>
                {/* Row 1: Company Name + Verified Icon */}
                <Flex align="center" gap="xs">
                  <Title order={3} fw={500}>
                    {company.legalName || "Company Name"}
                  </Title>
                  {company.status === "VERIFIED" && (
                    <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                  )}
                </Flex>

                {/* Row 2: GTID Badge */}
                {company.gtid && (
                  <Badge
                    variant="light"
                    size="sm"
                    radius="sm"
                    style={{
                      width: "fit-content",
                      backgroundColor: "#EEF2F7",
                      color: "#374151",
                    }}
                  >
                    {company.gtid}
                  </Badge>
                )}

                {/* Row 3: Location, Phone, Email */}
                <Flex gap="md" align="center" mt={4}>
                  <Flex align="center" gap={6}>
                    {headquartersFlagEmoji ? (
                      <Text
                        component="span"
                        fz={18}
                        lh={1}
                        role="img"
                        aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                      >
                        {headquartersFlagEmoji}
                      </Text>
                    ) : null}
                    <Text size="sm" c="slate.6">
                      {formatHeadquartersLocationLine(company.headquartersCity, company.headquartersCountry)}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={6}>
                    <IconPhone size={16} color="#6B7280" />
                    <Text size="sm" c="slate.6">
                      {company.phoneCountryCode && company.phoneNumber
                        ? `${company.phoneCountryCode} ${company.phoneNumber}`
                        : "N/A"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={6}>
                    <IconMail size={16} color="#6B7280" />
                    <Text size="sm" c="slate.6">
                      {company.email || "N/A"}
                    </Text>
                  </Flex>
                </Flex>

                {/* Row 4: Website and LinkedIn */}
                <Flex gap="md" align="center" mt={2}>
                  {company.website && (
                    <Anchor href={company.website} target="_blank" td="none">
                      <Flex align="center" gap={6}>
                        <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                        <Text size="sm" c="primary.6">
                          Website
                        </Text>
                        <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                      </Flex>
                    </Anchor>
                  )}
                  {company.linkedinUrl && (
                    <Anchor href={company.linkedinUrl} target="_blank" td="none">
                      <Flex align="center" gap={6}>
                        <IconBrandLinkedin size={16} color="var(--mantine-color-primary-6)" />
                        <Text size="sm" c="primary.6">
                          LinkedIn
                        </Text>
                      </Flex>
                    </Anchor>
                  )}
                </Flex>
              </Flex>
            </Flex>

            {/* Right side: Verification Status Badge + Action Buttons */}
            <Flex direction="column" align="flex-end" gap="md">
              {company.status !== "VERIFIED" && <VerificationStatusBadge status={company.status} />}

              {(showVerifyButton || showRejectButton) && (
                <Group gap="sm">
                  {showVerifyButton && (
                    <Button
                      variant="filled"
                      color="primary.6"
                      size="sm"
                      leftSection={<IconCheck size={16} />}
                      onClick={openVerifyModal}
                      disabled={actionLoading}
                    >
                      Verify
                    </Button>
                  )}
                  {showRejectButton && (
                    <Button
                      variant="outline"
                      color="red"
                      size="sm"
                      leftSection={<IconX size={16} />}
                      onClick={openRejectModal}
                      disabled={actionLoading}
                    >
                      Reject
                    </Button>
                  )}
                </Group>
              )}
              {showVerifyDirectlyButton && (
                <Group gap="sm">
                  <Button
                    variant="filled"
                    color="primary.6"
                    size="sm"
                    leftSection={<IconCheck size={16} />}
                    onClick={openVerifyDirectlyModal}
                    disabled={actionLoading}
                  >
                    Verify Directly
                  </Button>
                </Group>
              )}
            </Flex>
          </Flex>
        </Paper>

        {company?.status === "ACTION_REQUIRED" && company?.adminComments && (
          <Paper p="md" radius="lg" mb="xl" bg="#D84B4B33" style={{ border: "1px solid var(--mantine-color-red-3)" }}>
            <Flex gap="sm" align="flex-start">
              <Box mt={2}>
                <IconAlertCircle size={20} color="#D84B4B" />
              </Box>
              <Flex direction="column">
                <Text fz="sm" fw={600} c="#D84B4B">
                  Verification Rejected
                </Text>
                <Text fz="sm" c="#D84B4B">
                  {company.adminComments}
                </Text>
              </Flex>
            </Flex>
          </Paper>
        )}
        {/* Enable/Disable Banner - Only for verified profiles */}
        {company?.status === "VERIFIED" && (
          <Paper
            p="md"
            radius="lg"
            mb="xl"
            style={{
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
            }}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Badge variant="light" color={company.adminBlocked ? "#D84B4B" : "green"} size="md" mb="xs">
                  {company.adminBlocked ? "Disabled" : "Enabled"}
                </Badge>
                <Text size="sm" c="slate.6">
                  {company.adminBlocked
                    ? "This company is disabled and hidden from the Discover page."
                    : "This company is enable and visible on the Discover page with full platform access."}
                </Text>
              </Box>
              <Button
                size="sm"
                color={company.adminBlocked ? "primary" : "#D84B4B"}
                variant="filled"
                loading={blockLoading}
                onClick={handleToggleBlock}
              >
                {company.adminBlocked ? "Enable" : "Disable"}
              </Button>
            </Flex>
          </Paper>
        )}

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab} styles={adminCompanyNavTabsRootStyles}>
          <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
            <Tabs.List styles={adminCompanyNavTabsListStyles}>
              <Tabs.Tab
                value="profile"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "profile" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Company Profile
              </Tabs.Tab>
              <Tabs.Tab
                value="images"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "images" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Images and Documents
              </Tabs.Tab>
              <Tabs.Tab
                value="collaborations"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color:
                    activeTab === "collaborations" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Collaborations
              </Tabs.Tab>
              <Tabs.Tab
                value="interests"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color: activeTab === "interests" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Interest Requests
              </Tabs.Tab>
              <Tabs.Tab
                value="creditHistory"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color:
                    activeTab === "creditHistory" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                Credit History
              </Tabs.Tab>
              <Tabs.Tab
                value="signupLegalAcceptance"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color:
                    activeTab === "signupLegalAcceptance"
                      ? "var(--mantine-color-primary-7)"
                      : "var(--mantine-color-slate-6)",
                }}
              >
                Signup legal acceptance
              </Tabs.Tab>
              <Tabs.Tab
                value="companyAuthorityAcceptance"
                px="md"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  color:
                    activeTab === "companyAuthorityAcceptance"
                      ? "var(--mantine-color-primary-7)"
                      : "var(--mantine-color-slate-6)",
                }}
              >
                Company authority acceptance
              </Tabs.Tab>
            </Tabs.List>
          </Box>

          <Tabs.Panel value="profile" pt="xl">
            {activeTab === "profile" && <MatchedProfileTab company={company} />}
          </Tabs.Panel>

          <Tabs.Panel value="images" pt="xl">
            {activeTab === "images" && (
              <ImagesTabContent company={company} refreshCompany={async () => {}} isViewMode={true} activeTab={activeTab} />
            )}
            <Divider my="xl" />
            <DocumentsTab company={company} isMatchedView={true} />
          </Tabs.Panel>

          <Tabs.Panel value="collaborations" pt="xl">
            {activeTab === "collaborations" && <AdminCollaborationsTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="interests" pt="xl">
            {activeTab === "interests" && <AdminInterestsTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="creditHistory" pt="xl">
            {activeTab === "creditHistory" && <AdminCreditHistoryTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="signupLegalAcceptance" pt="xl">
            {activeTab === "signupLegalAcceptance" && (
              <AdminSignupLegalAcceptanceTab records={company.user?.signupLegalAcceptances ?? []} />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="companyAuthorityAcceptance" pt="xl">
            {activeTab === "companyAuthorityAcceptance" && (
              <AdminCompanyAuthorityAcceptanceTab records={company.authorityAcceptances ?? []} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Container>
      <VerifyCompanyModal
        opened={verifyModalOpened}
        onClose={closeVerifyModal}
        onConfirm={handleVerify}
        isLoading={actionLoading}
        companyName={company.legalName}
      />
      <VerifyCompanyModal
        opened={verifyDirectlyModalOpened}
        onClose={closeVerifyDirectlyModal}
        onConfirm={handleVerifyDirectly}
        isLoading={actionLoading}
        companyName={company.legalName}
      />

      <RejectCompanyModal
        opened={rejectModalOpened}
        onClose={closeRejectModal}
        onConfirm={handleReject}
        isLoading={actionLoading}
        companyName={company.legalName}
        title="Reject Company Profile"
        description={
          company.legalName
            ? `You are about to reject the profile for "${company.legalName}". Please provide a reason for rejection.`
            : "Please provide a reason for rejecting this company profile."
        }
        confirmLabel="Reject Profile"
      />
      <RejectCompanyModal
        opened={disableModalOpened}
        onClose={closeDisableModal}
        onConfirm={handleConfirmDisable}
        isLoading={blockLoading}
        companyName={company.legalName}
      />
    </Box>
  );
}
